import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { PresthygCard, PresthygPageHeader, PresthygButton, PresthygBadge } from '../components/presthyg';
import { UserCog, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, X, Save } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Administrateur', color: '#0E2D43', bg: '#E0F2FE' },
  { value: 'commercial', label: 'Commercial', color: '#167786', bg: '#ECFEFF' },
];

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', first_name: '', email: '', password: '', role: 'commercial' });

  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      navigate('/');
      return;
    }
    loadUsers();
  }, [currentUser, navigate]);

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (err) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editingId}`, payload);
        toast.success('Utilisateur modifie');
      } else {
        await api.post('/auth/register', formData);
        toast.success('Utilisateur cree');
      }
      resetForm();
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (u) => {
    setFormData({ name: u.name, first_name: u.first_name || '', email: u.email, password: '', role: u.role });
    setEditingId(u.id);
    setShowForm(true);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Supprimer ${u.name} (${u.email}) ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success('Utilisateur supprime');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}/toggle-active`);
      toast.success(u.is_active ? 'Compte desactive' : 'Compte reactive');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', first_name: '', email: '', password: '', role: 'commercial' });
    setEditingId(null);
    setShowForm(false);
  };

  const roleInfo = (role) => ROLES.find(r => r.value === role) || ROLES[1];

  if (loading) return <Layout><div className="flex items-center justify-center py-24"><div className="w-8 h-8 border-3 border-t-transparent animate-spin" style={{ borderColor: 'var(--ph-teal)', borderTopColor: 'transparent', borderRadius: '50%', borderWidth: '3px' }} /></div></Layout>;

  return (
    <Layout>
      <div data-testid="users-page">
        <PresthygPageHeader
          title="Gestion des utilisateurs"
          subtitle={`${users.length} utilisateur(s) enregistre(s)`}
          actions={
            !showForm && (
              <PresthygButton variant="teal" onClick={() => setShowForm(true)} data-testid="add-user-btn">
                <Plus size={16} /> Ajouter un utilisateur
              </PresthygButton>
            )
          }
        />

        {/* Form */}
        {showForm && (
          <PresthygCard className="p-6 mb-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold" style={{ color: 'var(--ph-navy)', fontFamily: 'var(--font-display)' }}>
                {editingId ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h3>
              <button onClick={resetForm} className="p-1.5 rounded-full hover:bg-gray-100">
                <X size={18} style={{ color: 'var(--ph-slate500)' }} />
              </button>
            </div>
            <form onSubmit={handleSubmit} data-testid="user-form">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ph-navy)' }}>Nom</label>
                  <input type="text" value={formData.name} required
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="user-name-input"
                    className="w-full px-4 py-2.5 border text-sm" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ph-navy)' }}>Prenom</label>
                  <input type="text" value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    data-testid="user-firstname-input"
                    className="w-full px-4 py-2.5 border text-sm" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ph-navy)' }}>Email</label>
                  <input type="email" value={formData.email} required
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="user-email-input"
                    className="w-full px-4 py-2.5 border text-sm" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ph-navy)' }}>
                    {editingId ? 'Nouveau mot de passe (laisser vide = inchange)' : 'Mot de passe'}
                  </label>
                  <input type="password" value={formData.password} required={!editingId}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    data-testid="user-password-input"
                    className="w-full px-4 py-2.5 border text-sm" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ph-navy)' }}>Role</label>
                  <select value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    data-testid="user-role-select"
                    className="w-full px-4 py-2.5 border text-sm" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-3">
                <PresthygButton type="submit" variant="teal" data-testid="save-user-btn">
                  <Save size={16} /> {editingId ? 'Enregistrer' : 'Creer'}
                </PresthygButton>
                <PresthygButton type="button" variant="secondary" onClick={resetForm}>Annuler</PresthygButton>
              </div>
            </form>
          </PresthygCard>
        )}

        {/* Users table */}
        <PresthygCard className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--ph-slate50)' }}>
                <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--ph-slate500)' }}>Nom</th>
                <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--ph-slate500)' }}>Email</th>
                <th className="px-5 py-3 text-center font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--ph-slate500)' }}>Role</th>
                <th className="px-5 py-3 text-center font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--ph-slate500)' }}>Statut</th>
                <th className="px-5 py-3 text-right font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--ph-slate500)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const role = roleInfo(u.role);
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t" style={{ borderColor: 'var(--ph-slate200)' }}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center text-sm font-bold" style={{ borderRadius: 'var(--ph-radius-sm)', backgroundColor: role.bg, color: role.color }}>
                          {(u.first_name || u.name || 'U')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold" style={{ color: 'var(--ph-navy)' }}>{u.first_name ? `${u.first_name} ${u.name}` : u.name}</p>
                          {isSelf && <span className="text-xs" style={{ color: 'var(--ph-teal)' }}>(vous)</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4" style={{ color: 'var(--ph-slate700)' }}>{u.email}</td>
                    <td className="px-5 py-4 text-center">
                      <span className="text-xs font-semibold px-3 py-1 inline-flex" style={{ borderRadius: 'var(--ph-radius-pill)', backgroundColor: role.bg, color: role.color }}>
                        {role.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <PresthygBadge status={u.is_active ? 'compliant' : 'error'}>
                        {u.is_active ? 'Actif' : 'Inactif'}
                      </PresthygBadge>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => handleEdit(u)} className="p-2 rounded-lg hover:bg-blue-50" title="Modifier" data-testid={`edit-user-${u.id}`}>
                          <Edit2 size={15} style={{ color: 'var(--ph-teal)' }} />
                        </button>
                        {!isSelf && (
                          <>
                            <button onClick={() => handleToggleActive(u)} className="p-2 rounded-lg hover:bg-amber-50" title={u.is_active ? 'Desactiver' : 'Activer'} data-testid={`toggle-user-${u.id}`}>
                              {u.is_active ? <ToggleRight size={15} style={{ color: '#059669' }} /> : <ToggleLeft size={15} style={{ color: '#DC2626' }} />}
                            </button>
                            <button onClick={() => handleDelete(u)} className="p-2 rounded-lg hover:bg-red-50" title="Supprimer" data-testid={`delete-user-${u.id}`}>
                              <Trash2 size={15} style={{ color: '#DC2626' }} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </PresthygCard>
      </div>
    </Layout>
  );
};

export default UsersPage;
